import type { ColorsTheme } from '@alltrails/types/ColorsTheme';

/**
 * An explicit theme choice is stored in a cookie rather than localStorage so the server can read it while rendering
 * and set the attribute below on the initial HTML. localStorage is only readable after hydration, which would paint
 * the default theme first and then flip it.
 *
 * The cookie is only written once the user picks a theme. No cookie means 'system': the OS setting decides, which the
 * server cannot read, so RootLayout resolves it in the browser before first paint instead.
 *
 * Keep this cookie small and few in number: cookies are sent on every request to the host. Preferences the server
 * does not render (map layer visibility, for example) belong in localStorage instead.
 */
export const COLORS_THEME_COOKIE_NAME = 'at_colors_theme';

/** Denali keys its palettes off this attribute — see packages/denali/styles/colors-light.css and colors-dark.css. */
export const COLORS_THEME_ATTRIBUTE = 'data-at-theme-colors';

/** Matches the OS/browser setting behind the 'system' preference. */
export const DARK_COLORS_MEDIA_QUERY = '(prefers-color-scheme: dark)';

const EXPIRATION_DAYS = 365;

/**
 * Attributes for the browser-side write. Host-scoped on purpose, so the cookie is not sent to every subdomain.
 * The path has to match on removal too, or clearing back to 'system' leaves the old cookie in place.
 */
export const colorsThemeCookieAttributes = () => ({
  expires: EXPIRATION_DAYS,
  path: '/',
  sameSite: 'lax' as const,
  // Deployed environments are all https; dev runs over http, where a secure cookie would be dropped silently.
  secure: window.location.protocol === 'https:'
});

/**
 * Resolves 'system' against the OS setting in the browser, for RootLayout to run in `<head>` before the body is
 * parsed — the server cannot read `prefers-color-scheme`, and anything that waits for hydration paints first.
 *
 * Built only from the two constants above. **Never interpolate a dynamic value here:** this string is emitted into
 * the page as executable script, so anything derived from a request, a cookie or user input would be an injection
 * vector. ColorsThemeProvider owns the attribute from hydration onwards.
 */
export const COLORS_THEME_SCRIPT = `try{document.documentElement.setAttribute('${COLORS_THEME_ATTRIBUTE}',window.matchMedia('${DARK_COLORS_MEDIA_QUERY}').matches?'dark':'light')}catch(e){}`;

/** Narrows an arbitrary cookie value, so a stale or hand-edited cookie falls back to 'system'. */
export const parseColorsTheme = (value?: string): ColorsTheme | undefined => {
  if (value === 'light' || value === 'dark') {
    return value;
  }

  return undefined;
};
